import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";
import axios from "axios";
import { IoGiftOutline, IoRocketOutline } from "react-icons/io5";
import { t as i18nT } from "../../i18n";
import { useInterfaceLanguage } from "../../hooks/useInterfaceLanguage";


const CREATE_GAME_MODE_OPTIONS = [
  {
    id: "free",
    titleKey: "scienceModeTitle",
    subtitleKey: "scienceModeSubtitle",
    detailKey: "scienceModeDetail",
    icon: "/mode-icons/free.png",
    className: "xima-create-mode-science",
  },
  {
    id: "dice",
    titleKey: "chanceModeTitle",
    subtitleKey: "chanceModeSubtitle",
    detailKey: "chanceModeDetail",
    icon: "/mode-icons/dice.png",
    className: "xima-create-mode-luck",
  },
  {
    id: "cienzar",
    titleKey: "cienzarModeTitle",
    subtitleKey: "cienzarModeSubtitle",
    detailKey: "cienzarModeDetail",
    icon: "/mode-icons/cienzar.png",
    className: "xima-create-mode-cienzar",
  },
  {
    id: "sciencevsazar",
    titleKey: "scienceVsAzarModeTitle",
    subtitleKey: "scienceVsAzarModeSubtitle",
    detailKey: "scienceVsAzarModeDetail",
    icon: "/mode-icons/cienzar.png",
    className: "xima-create-mode-sciencevsazar",
  },
];

const CREATE_GAME_PLAYER_OPTIONS = [
  {
    value: 2,
    labelKey: "oneVsOne",
    detailKey: "twoPlayers",
    icon: "/mode-icons/players-1v1.png",
  },
  {
    value: 3,
    labelKey: "oneVsTwo",
    detailKey: "threePlayers",
    icon: "/mode-icons/players-1v2.png",
  },
  {
    value: 4,
    labelKey: "oneVsThree",
    detailKey: "fourPlayers",
    icon: "/mode-icons/players-1v3.png",
  },
];

const CREATE_GAME_TIME_OPTIONS = [1, 2, 5, 10];
const PENDING_LOBBY_CREATE_STORAGE_KEY = "xima_pending_lobby_create_v1";
const CREATE_GAME_PREFERENCES_STORAGE_KEY = "xima_create_game_preferences_v1";
const CREATE_GAME_DIFFICULTY_OPTIONS = [
  { value: "easy", labelKey: "easy", detailKey: "easyDetail" },
  { value: "medium", labelKey: "medium", detailKey: "mediumDetail" },
  { value: "hard", labelKey: "hard", detailKey: "hardDetail" },
  { value: "injusto", labelKey: "unfair", detailKey: "unfairDetail" },
];

const clampPlayers = (value) => Math.max(2, Math.min(4, Number(value) || 2));
const clampCreateZoom = (value) => Math.min(1.08, Math.max(0.76, value));
const CREATE_GAME_DEFAULT_PREFERENCES = {
  mode: "cienzar",
  players: 2,
  turnTime: 5,
  difficulty: "injusto",
};

const normalizeCreateMode = (value) =>
  CREATE_GAME_MODE_OPTIONS.some((option) => option.id === value)
    ? value
    : CREATE_GAME_DEFAULT_PREFERENCES.mode;

const normalizeCreateTime = (value) => {
  const parsed = Number(value);
  return CREATE_GAME_TIME_OPTIONS.includes(parsed) ? parsed : CREATE_GAME_DEFAULT_PREFERENCES.turnTime;
};

const normalizeCreateDifficulty = (value) =>
  CREATE_GAME_DIFFICULTY_OPTIONS.some((option) => option.value === value)
    ? value
    : CREATE_GAME_DEFAULT_PREFERENCES.difficulty;

const readCreateGamePreferences = () => {
  if (typeof window === "undefined") {
    return CREATE_GAME_DEFAULT_PREFERENCES;
  }

  try {
    const rawValue = window.localStorage.getItem(CREATE_GAME_PREFERENCES_STORAGE_KEY);
    if (!rawValue) {
      return CREATE_GAME_DEFAULT_PREFERENCES;
    }
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") {
      return CREATE_GAME_DEFAULT_PREFERENCES;
    }

    return {
      mode: normalizeCreateMode(parsed.mode),
      players: clampPlayers(parsed.players),
      turnTime: normalizeCreateTime(parsed.turnTime),
      difficulty: normalizeCreateDifficulty(parsed.difficulty),
    };
  } catch {
    return CREATE_GAME_DEFAULT_PREFERENCES;
  }
};

const writeCreateGamePreferences = (preferences) => {
  if (typeof window === "undefined") return;
  try {
    const payload = {
      mode: normalizeCreateMode(preferences?.mode),
      players: clampPlayers(preferences?.players),
      turnTime: normalizeCreateTime(preferences?.turnTime),
      difficulty: normalizeCreateDifficulty(preferences?.difficulty),
    };
    window.localStorage.setItem(
      CREATE_GAME_PREFERENCES_STORAGE_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // Ignore storage failures.
  }
};

const CreateGame = () => {
  const navigate = useNavigate();
  useInterfaceLanguage();
  const screenRef = useRef(null);
  const [mode, setMode] = useState(() => readCreateGamePreferences().mode);
  const [players, setPlayers] = useState(() => readCreateGamePreferences().players);
  const [turnTime, setTurnTime] = useState(() => readCreateGamePreferences().turnTime);
  const [difficulty, setDifficulty] = useState(
    () => readCreateGamePreferences().difficulty,
  );
  const [interfaceZoom, setInterfaceZoom] = useState(1);
  const [creatingChallenge, setCreatingChallenge] = useState(false);

  useEffect(() => {
    const screen = screenRef.current;
    if (!screen) {
      return undefined;
    }

    const handleWheelZoom = (event) => {
      if (!event.ctrlKey) {
        return;
      }

      event.preventDefault();
      setInterfaceZoom((currentZoom) => {
        const nextZoom = currentZoom + (event.deltaY < 0 ? 0.05 : -0.05);
        return Number(clampCreateZoom(nextZoom).toFixed(2));
      });
    };

    screen.addEventListener("wheel", handleWheelZoom, { passive: false });

    return () => {
      screen.removeEventListener("wheel", handleWheelZoom);
    };
  }, []);

  useEffect(() => {
    writeCreateGamePreferences({
      mode,
      players,
      turnTime,
      difficulty,
    });
  }, [mode, players, turnTime, difficulty]);

  const startCreateFlow = async () => {
    if (creatingChallenge) {
      return;
    }

    const selectedMode = String(mode || "dice").trim().toLowerCase();
    const selectedPlayers = clampPlayers(players);
    const selectedTime = Number(turnTime) || 5;
    const selectedDifficulty = normalizeCreateDifficulty(difficulty);
    const requestKey = `create-game-${Date.now()}`;
    const lobbyCreatePayload = {
      playType: "online",
      mode: selectedMode,
      memberSize: selectedPlayers,
      havingTime: selectedTime,
      botDifficulty: selectedDifficulty,
      requestKey,
      autoCreate: true,
      createdAt: Date.now(),
    };

    try {
      window.sessionStorage.setItem(
        PENDING_LOBBY_CREATE_STORAGE_KEY,
        JSON.stringify(lobbyCreatePayload)
      );
    } catch (error) {
      console.log(error);
    }

    const params = new URLSearchParams({
      quickView: "independientes",
      fromLobbyCreate: "1",
      fromLobbySource: "independientes",
      lobbyCreatePlayType: "online",
      lobbyCreateMode: selectedMode,
      lobbyCreatePlayers: String(selectedPlayers),
      lobbyCreateTime: String(selectedTime),
      lobbyCreateDifficulty: selectedDifficulty,
      lobbyCreateAuto: "0",
      lobbyCreateRequestKey: requestKey,
    });

    try {
      setCreatingChallenge(true);
      const response = await axios.post("/tournaments", {
        tournamentName: "",
        mode: selectedMode,
        memberSize: selectedPlayers,
        havingTime: selectedTime,
        botDifficulty: selectedDifficulty,
        duration: 1,
        startingTime: new Date(Date.now() + 1000).toISOString(),
        creationSection: "independientes",
        isIndependentMatch: true,
        isQuickMatch: false,
        hasMatchPassword: false,
        matchPassword: "",
        tournamentPassword: "",
        joinPassword: "",
        tournamentLifetimeMinutes: 0,
      });

      const created = response?.data || null;
      const tournamentId = String(created?._id || "").trim();

      if (tournamentId) {
        params.set("createdTournamentId", tournamentId);
      }

      try {
        window.sessionStorage.removeItem(PENDING_LOBBY_CREATE_STORAGE_KEY);
      } catch (error) {
        console.log(error);
      }

      const navigationState = {
        openMatchmakingQuickView: "independientes",
        openMatchmakingQuickViewRequestKey: requestKey,
        fromLobbyCreate: "1",
        fromLobbySource: "independientes",
        source: "lobbyCreate",
        lobbyCreateAuto: "0",
        lobbyCreatePreferences: {
          ...lobbyCreatePayload,
          autoCreate: false,
        },
        lobbyCreatedIndependentChallenge: created,
        createdTournamentId: tournamentId,
      };

      if (tournamentId) {
        navigate(`/game/${tournamentId}?autoplay=1`, {
          state: navigationState,
        });
        return;
      }

      navigate(`/game?${params.toString()}`, {
        state: navigationState,
      });
    } catch (error) {
      console.error("Error creating independent challenge:", error);
      notification.error({
        message: i18nT("match.noSePudoCrearElDesafio"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          i18nT("match.retryCreateMatch"),
      });
    } finally {
      setCreatingChallenge(false);
    }
  };

  const openChampionship = () => {
    navigate("/game?quickView=torneos&tournamentMode=free&fromLobbyTournament=1&fromLobbySource=torneos", {
      state: {
        fromLobbyTournament: "1",
        fromLobbySource: "torneos",
        source: "lobbyTournament",
        tournamentSection: "12",
        tournamentMode: "free",
      },
    });
  };

  return (
    <main
      ref={screenRef}
      className="xima-create-game-screen"
      style={{ "--xima-create-zoom": String(interfaceZoom) }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="xima-create-game-overlay" aria-hidden="true" />
      <div className="xima-create-game-shell">
        <header className="xima-create-game-header">
          <div className="xima-create-game-logo" aria-label="XIMA">
            XiMA
          </div>
          <button
            type="button"
            onClick={() => navigate("/game/lobby")}
            className="xima-create-back-button"
          >
            <span aria-hidden="true">&lt;</span>
            {i18nT("match.volverAlLobby")}
          </button>
          <div className="xima-create-game-title">
            <h1>{i18nT("match.crearNuevoJuego")}</h1>
            <p>{i18nT("match.eligeTusPreferenciasYDesafiaAOtrosJugadores")}</p>
          </div>
        </header>

        <div className="xima-create-controls-stage">
          <div className="xima-create-game-hero-space" aria-hidden="true" />

          <section className="xima-create-mode-grid" aria-label={i18nT("match.modoDeJuego")}>
            {CREATE_GAME_MODE_OPTIONS.map((option) => {
              const selected = mode === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setMode(option.id)}
                  className={`xima-create-mode-card ${option.className}${
                    selected ? " xima-create-selected" : ""
                  }`}
                  aria-pressed={selected}
                >
                  <span className="xima-create-mode-icon">
                    <img
                      src={option.icon}
                      alt=""
                      aria-hidden="true"
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  </span>
                  <span className="xima-create-mode-copy">
                    <strong>{i18nT(`match.${option.titleKey}`)}</strong>
                    <small>{i18nT(`match.${option.subtitleKey}`)}</small>
                    <small>{i18nT(`match.${option.detailKey}`)}</small>
                  </span>
                </button>
              );
            })}
          </section>

          <section className="xima-create-options-grid">
            <div className="xima-create-option-panel">
              <div className="xima-create-panel-heading">
                <strong>{i18nT("match.jugadores")}</strong>
                <span>{i18nT("match.eligeLaModalidadDeJuego")}</span>
              </div>
              <div className="xima-create-choice-grid xima-create-player-grid">
                {CREATE_GAME_PLAYER_OPTIONS.map((option) => {
                  const selected = Number(players) === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setPlayers(option.value)}
                      className={`xima-create-choice-button${selected ? " xima-create-selected" : ""}`}
                      aria-pressed={selected}
                    >
                      <img
                        src={option.icon}
                        alt=""
                        aria-hidden="true"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                      <strong>{i18nT(`match.${option.labelKey}`)}</strong>
                      <small>{i18nT(`match.${option.detailKey}`)}</small>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="xima-create-option-panel">
              <div className="xima-create-panel-heading">
                <strong>{i18nT("match.tiempoPorTurno")}</strong>
                <span>{i18nT("match.eligeElTiempoParaCadaTurno")}</span>
              </div>
              <div className="xima-create-choice-grid xima-create-time-grid">
                {CREATE_GAME_TIME_OPTIONS.map((value) => {
                  const selected = Number(turnTime) === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTurnTime(value)}
                      className={`xima-create-choice-button xima-create-time-button${
                        selected ? " xima-create-selected" : ""
                      }`}
                      aria-pressed={selected}
                    >
                      <strong>{i18nT("match.minutesShort", { value })}</strong>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="xima-create-option-panel">
              <div className="xima-create-panel-heading">
                <strong>{i18nT("match.dificultad")}</strong>
                <span>{i18nT("match.eligeElNivelDeDificultadDelJuego")}</span>
              </div>
              <div className="xima-create-choice-grid xima-create-difficulty-grid">
                {CREATE_GAME_DIFFICULTY_OPTIONS.map((option) => {
                  const selected = difficulty === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setDifficulty(option.value)}
                      className={`xima-create-choice-button${selected ? " xima-create-selected" : ""}`}
                      aria-pressed={selected}
                    >
                      <strong>{i18nT(`match.${option.labelKey}`)}</strong>
                      <small>{i18nT(`match.${option.detailKey}`)}</small>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="xima-create-action-grid">
            <button
              type="button"
              onClick={() => navigate("/game/store?fromLobbySource=store")}
              className="xima-create-promo-card xima-create-store-card"
            >
              <span className="xima-create-promo-icon" aria-hidden="true">
                <IoGiftOutline />
              </span>
              <span>
                <strong>{i18nT("match.irALaTienda")}</strong>
                <small>{i18nT("match.consigueLaNuevaEdicionYMejorasExclusivas")}</small>
              </span>
              <img src="/store/xima-board/xima-board-0.png" alt="" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={startCreateFlow}
              className="xima-create-submit-button"
              disabled={creatingChallenge}
              aria-busy={creatingChallenge}
            >
              <span aria-hidden="true">+</span>
              {creatingChallenge ? i18nT("match.creating") : i18nT("match.createGameButton")}
            </button>

            <button
              type="button"
              onClick={openChampionship}
              className="xima-create-promo-card xima-create-championship-card"
            >
              <span className="xima-create-promo-icon" aria-hidden="true">
                <IoRocketOutline />
              </span>
              <span>
                <strong>{i18nT("match.campeonatoMundialXima")}</strong>
                <small>{i18nT("match.seParteDelTorneoMasGrandeYDemuestra")}</small>
              </span>
            </button>
          </section>
        </div>
      </div>
    </main>
  );
};

export default CreateGame;
